import React, { ChangeEvent, CSSProperties, ReactNode, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import {
  Control,
  Controller,
  FieldValues,
  UseFormRegister,
  UseFormSetFocus,
} from 'react-hook-form';
import { InputContainer } from '../styles';

type CaseInput = 'lower' | 'upper' | 'normal';

const INPUT_WRAPPER_STYLE: CSSProperties = {
  position: 'relative',
  width: '100%',
};

const RIGHT_ELEMENT_STYLE: CSSProperties = {
  position: 'absolute',
  top: '50%',
  right: '12px',
  transform: 'translateY(-50%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

interface InputTextProps extends React.HTMLProps<HTMLInputElement> {
  label?: string;
  placeholder?: string;
  name: string;
  register: UseFormRegister<FieldValues>;
  isError: boolean;
  maxLength: number;
  caseInput?: CaseInput;
  toLowerCase?: boolean | undefined;
  isDisabled?: boolean;
  control?: Control<FieldValues, any>;
  setFocus?: UseFormSetFocus<FieldValues>;
  alignRight?: boolean;
  rightElement?: ReactNode;
}

const getInputClassName = (
  isError: boolean,
  alignRight: boolean,
  className?: string,
) =>
  [
    'form-control',
    isError ? 'is-invalid' : '',
    alignRight ? 'align-right' : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

const getInputStyle = (
  style: CSSProperties | undefined,
  rightElement?: ReactNode,
) => {
  if (!rightElement) {
    return style;
  }

  return {
    ...style,
    paddingRight: '44px',
  };
};

const getCursorPosition = (input: HTMLInputElement) => {
  const { value, selectionStart, selectionEnd } = input;
  const lowerCaseValue = value.toLowerCase();

  const cursorPosition =
    selectionStart === selectionEnd ? selectionStart : selectionEnd;
  const cursorOffset = lowerCaseValue.length - value.length;

  return (cursorPosition || 0) + cursorOffset;
};

const setInputCursorPosition = (
  input: HTMLInputElement,
  cursorPosition: number | null,
) => {
  if (cursorPosition === null) {
    return;
  }

  try {
    input.setSelectionRange(cursorPosition, cursorPosition);
  } catch (error) {
    console.log('[Error] erro ao tentar restaurar posição do cursor: ', error);
  }
};

const restoreCursorPosition = (
  input: HTMLInputElement,
  cursorPosition: number,
) => {
  setTimeout(() => {
    if (document.activeElement === input) {
      setInputCursorPosition(input, cursorPosition);
    }
  }, 0);
};

const applyCaseTransform = (value: string, type: CaseInput) => {
  if (type === 'upper') {
    return value.toUpperCase();
  }

  if (type === 'lower') {
    return value.toLowerCase();
  }

  return value;
};

const applyControlledCaseTransform = (
  event: ChangeEvent<HTMLInputElement>,
  type: CaseInput,
) => {
  const { selectionStart } = event.target;
  const transformedValue = applyCaseTransform(event.target.value, type);

  event.target.value = transformedValue;
  setInputCursorPosition(event.target, selectionStart);

  return transformedValue;
};

const applyLegacyTransform = (value: string, shouldLowerCase: boolean) => {
  if (shouldLowerCase) {
    return value.toLowerCase().trimStart();
  }

  return value.toUpperCase();
};

export const InputText: React.FC<InputTextProps> = ({
  label = '',
  placeholder = '',
  name,
  register,
  setFocus,
  isError,
  maxLength,
  caseInput = 'normal',
  toLowerCase = true,
  control,
  isDisabled = false,
  alignRight = false,
  rightElement,
  className,
  style,
  disabled,
  onChange,
  ...rest
}) => {
  useEffect(() => {
    if (setFocus) {
      setFocus(name);
    }
  }, [name, setFocus]);

  const registeredField = register(name);
  const resolvedDisabled = disabled ?? isDisabled;
  const inputClassName = getInputClassName(isError, alignRight, className);
  const inputStyle = getInputStyle(style, rightElement);
  const inputProps = {
    ...registeredField,
    ...rest,
    maxLength,
    className: inputClassName,
    placeholder,
    disabled: resolvedDisabled,
    style: inputStyle,
  };

  return (
    <InputContainer>
      <Form.Group>
        <Form.Label>{label}</Form.Label>

        <div style={INPUT_WRAPPER_STYLE}>
          {control ? (
            <Controller
              name={name}
              control={control}
              render={({ field: { value, onChange: fieldOnChange } }) => {
                return (
                  <input
                    {...inputProps}
                    value={value ?? ''}
                    onChange={
                      onChange ||
                      ((event: ChangeEvent<HTMLInputElement>) => {
                        const nextValue =
                          caseInput !== 'normal'
                            ? applyControlledCaseTransform(event, caseInput)
                            : event.target.value;

                        fieldOnChange(nextValue);
                      })
                    }
                  />
                );
              }}
            />
          ) : (
            <input
              {...inputProps}
              onChange={
                onChange ||
                ((event: ChangeEvent<HTMLInputElement>) => {
                  if (toLowerCase === undefined) {
                    return;
                  }

                  const cursorPosition = getCursorPosition(event.target);
                  const transformedValue = applyLegacyTransform(
                    event.target.value,
                    toLowerCase,
                  );

                  restoreCursorPosition(event.target, cursorPosition);
                  event.target.value = transformedValue;
                })
              }
            />
          )}

          {rightElement && (
            <div style={RIGHT_ELEMENT_STYLE}>{rightElement}</div>
          )}
        </div>
      </Form.Group>
    </InputContainer>
  );
};
